import { useState } from "react";
import type { FormEvent } from "react";
import { Aviso, Botao, Campo } from "../ui";
import type { DesafioDeMfa } from "../../nucleo/api";
import {
  confirmarInscricaoNoLogin,
  iniciarInscricaoNoLogin,
  verificarSegundoFator,
} from "../../nucleo/acesso";
import { mensagemDoConsole } from "../../nucleo/console";
import { ConfigurarSegundoFator } from "./ConfigurarSegundoFator";

/** O código do aplicativo autenticador: 6 dígitos. Código de recuperação não segue este formato. */
const CODIGO_DO_APLICATIVO = /^\d{6}$/;

export interface PropsDoPassoDoSegundoFator {
  desafio: DesafioDeMfa;
  /** O cookie de sessão já existe: falta só carregar quem entrou e seguir. */
  aoConcluir: () => void;
  aoVoltar: () => void;
}

/**
 * O que a entrada pede DEPOIS da senha certa: o código do aplicativo (`verificar`) ou, quando a
 * política da organização exige e a pessoa ainda não tem, configurar o aplicativo (`inscrever`).
 * Nenhuma sessão existe até este passo terminar.
 */
export function PassoDoSegundoFator({ desafio, aoConcluir, aoVoltar }: PropsDoPassoDoSegundoFator) {
  if (desafio.etapa === "inscrever") {
    return (
      <div data-testid="passo-inscrever">
        <Aviso tom="marca" className="mb-4">
          A sua organização exige a verificação em duas etapas. Configure o aplicativo agora para
          concluir a entrada.
        </Aviso>
        <ConfigurarSegundoFator
          chave={["mfa", "login", desafio.desafio]}
          iniciar={() => iniciarInscricaoNoLogin(desafio.desafio)}
          confirmar={(codigo) => confirmarInscricaoNoLogin(desafio.desafio, codigo)}
          aoConcluir={aoConcluir}
          rotuloDoFim="Entrar no portal"
        />
        <p className="acesso__ajuda">
          <Botao variante="fantasma" tamanho="pequeno" onClick={aoVoltar}>
            Voltar e entrar com outra conta
          </Botao>
        </p>
      </div>
    );
  }
  return <VerificarCodigo desafio={desafio} aoConcluir={aoConcluir} aoVoltar={aoVoltar} />;
}

function VerificarCodigo({ desafio, aoConcluir, aoVoltar }: PropsDoPassoDoSegundoFator) {
  const [codigo, setCodigo] = useState("");
  const [recuperacao, setRecuperacao] = useState(false);
  const [erro, setErro] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  async function conferir(valor: string) {
    setErro(null);
    setEnviando(true);
    try {
      await verificarSegundoFator(desafio.desafio, valor.trim());
      aoConcluir();
    } catch (causa) {
      setErro(mensagemDoConsole(causa));
      setEnviando(false);
    }
  }

  function enviar(evento: FormEvent<HTMLFormElement>) {
    evento.preventDefault();
    void conferir(codigo);
  }

  return (
    <form onSubmit={enviar} noValidate data-testid="passo-verificar">
      <Campo
        rotulo={recuperacao ? "Código de recuperação" : "Código do aplicativo"}
        inputMode={recuperacao ? "text" : "numeric"}
        autoComplete="one-time-code"
        placeholder={recuperacao ? "xxxxx-xxxxx" : "000000"}
        ajuda={
          recuperacao
            ? "Cada código de recuperação vale uma vez."
            : "Abra o aplicativo autenticador e digite o código de 6 dígitos."
        }
        value={codigo}
        onChange={(evento) => {
          const valor = evento.target.value;
          setCodigo(valor);
          // Os 6 dígitos do aplicativo fecham o passo sozinhos: o botão era um clique que não
          // protegia nada (o servidor confere o mesmo código, uma vez só, com o mesmo freio).
          if (!recuperacao && CODIGO_DO_APLICATIVO.test(valor.trim())) void conferir(valor);
        }}
        disabled={enviando}
        autoFocus
        obrigatorio
        data-testid="codigo-do-segundo-fator"
      />
      {erro ? (
        <Aviso tom="erro" className="mb-4" data-testid="erro-do-segundo-fator">
          {erro}
        </Aviso>
      ) : null}
      <Botao type="submit" variante="verde" larguraTotal disabled={enviando || codigo.trim() === ""}>
        {enviando ? "Conferindo…" : "Confirmar"}
      </Botao>
      <p className="acesso__ajuda">
        <Botao
          variante="fantasma"
          tamanho="pequeno"
          onClick={() => {
            setRecuperacao((atual) => !atual);
            setCodigo("");
            setErro(null);
          }}
        >
          {recuperacao
            ? "Usar o código do aplicativo"
            : "Perdi o celular: usar um código de recuperação"}
        </Botao>{" "}
        <Botao variante="fantasma" tamanho="pequeno" onClick={aoVoltar}>
          Voltar
        </Botao>
      </p>
    </form>
  );
}
